import * as React from 'react';

// Types of drawers used across the app
export type DrawerType =
  | 'fixture'
  | 'circuit'
  | 'control'
  | 'panel'
  | 'room'
  | 'selection'
  | 'settings'
  | 'ai';

interface DrawerState {
  activeDrawer: DrawerType | null;
  drawers: Record<DrawerType, boolean>;
}

interface DrawerContextValue extends DrawerState {
  openDrawer: (drawer: DrawerType) => void;
  closeDrawer: (drawer: DrawerType) => void;
  toggleDrawer: (drawer: DrawerType) => void;
  closeAllDrawers: () => void;
}

const DrawerContext = React.createContext<DrawerContextValue | undefined>(undefined);

export const useDrawers = () => {
  const ctx = React.useContext(DrawerContext);
  if (!ctx) throw new Error('useDrawers must be used within DrawerProvider');
  return ctx;
};

interface DrawerProviderProps {
  children: React.ReactNode;
}

export function DrawerProvider({ children }: DrawerProviderProps) {
  const [state, setState] = React.useState<DrawerState>(() => ({
    activeDrawer: null,
    drawers: {
      fixture: false,
      circuit: false,
      control: false,
      panel: false,
      room: false,
      selection: false,
      settings: false,
      ai: false,
    },
  }));

  const openDrawer = React.useCallback((drawer: DrawerType) => {
    setState(prev => ({
      activeDrawer: drawer,
      drawers: { ...prev.drawers, [drawer]: true },
    }));
  }, []);

  const closeDrawer = React.useCallback((drawer: DrawerType) => {
    setState(prev => ({
      activeDrawer: prev.activeDrawer === drawer ? null : prev.activeDrawer,
      drawers: { ...prev.drawers, [drawer]: false },
    }));
  }, []);

  const toggleDrawer = React.useCallback((drawer: DrawerType) => {
    setState(prev => {
      const isOpening = !prev.drawers[drawer];
      return {
        activeDrawer: isOpening ? drawer : prev.activeDrawer === drawer ? null : prev.activeDrawer,
        drawers: { ...prev.drawers, [drawer]: isOpening },
      };
    });
  }, []);

  const closeAllDrawers = React.useCallback(() => {
    setState(prev => ({
      activeDrawer: null,
      drawers: Object.fromEntries(Object.keys(prev.drawers).map(d => [d, false])) as Record<
        DrawerType,
        boolean
      >,
    }));
  }, []);

  const value: DrawerContextValue = {
    ...state,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    closeAllDrawers,
  };

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
}
